import React, {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  ReactNode,
} from 'react';
import styled from 'styled-components';
import colors from '../../constants/colors.constants';

export interface ButtonProps {
  style?: CSSProperties;
  onClick?: () => void;
  children?: ReactNode;
  disabled?: boolean;
  size?: 'default' | 'medium' | 'small';
  type?: 'black' | 'gray' | 'lightGray' | 'dark' | 'primary';
  badge?: number;
  className?: string;
  id?: string;
}
const Button = forwardRef(
  (props: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
    const {
      badge = 0,
      disabled = false,
      style,
      children,
      onClick,
      size = 'default',
      type = 'primary',
      id,
    } = props;

    return (
      <StyledButton
        $size={size}
        $type={type}
        disabled={disabled}
        style={style}
        ref={ref}
        onClick={onClick}
        id={id}
      >
        <div
          className="flex items-center justify-center gap-2"
          style={{
            width: '100%',
          }}
        >
          {children}
          {badge > 0 && (
            <span
              style={{
                color: colors.btnBlackBg,
                background: colors.btnBlackText,
                borderRadius: 12,
                minWidth: 24,
                fontSize: 14,
                padding: '4px 0',
                fontWeight: 600,
              }}
            >
              {badge}
            </span>
          )}
        </div>
      </StyledButton>
    );
  },
);

export default Button;

const StyledButton = styled.button<{
  $size: 'default' | 'medium' | 'small';
  $type: 'black' | 'gray' | 'lightGray' | 'dark' | 'primary';
}>`
  font-weight: 500;
  border-radius: 12px;
  outline: none;
  -webkit-tap-highlight-color: transparent;

  height: ${(props) => {
    if (props.$size === 'small') {
      return '48px';
    }
    return '56px';
  }};

  color: ${(props) => {
    if (props.disabled) {
      return colors.btnDisabledText;
    }
    if (props.$type === 'black') {
      return colors.btnBlackText;
    }
    if (props.$type === 'lightGray') {
      return colors.slate500;
    }
    return colors.btnBlackText;
  }};

  font-size: ${(props) => {
    if (props.$size === 'medium') {
      return '16px';
    }

    if (props.$size === 'small') {
      return '15px';
    }
    return '16px';
  }};

  padding: ${(props) => {
    if (props.$size === 'medium') {
      return '0 28px';
    }

    if (props.$size === 'small') {
      return '0 16px';
    }
    return '0 16px';
  }};

  background: ${(props) => {
    if (props.disabled) {
      return colors.btnDisabledBg;
    }
    if (props.$type === 'black') {
      return colors.btnBlackBg;
    }
    if (props.$type === 'primary') {
      return colors.primary3;
    }
    if (props.$type === 'gray') {
      return colors.subTitle2;
    }
    if (props.$type === 'lightGray') {
      return colors.slate200;
    }
    if (props.$type === 'dark') {
      return colors.slate700;
    }
    return colors.btnBlackBg;
  }};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${(props) => {
        if (props.disabled) {
          return colors.btnDisabledBg;
        }
        if (props.$type === 'black') {
          return colors.btnBlackBg;
        }
        if (props.$type === 'primary') {
          return colors.primary3;
        }
        if (props.$type === 'gray') {
          return colors.subTitle2;
        }
        if (props.$type === 'lightGray') {
          return colors.slate200;
        }
        if (props.$type === 'dark') {
          return colors.slate700;
        }
        return colors.btnBlackBg;
      }};
      color: ${(props) => {
        if (props.disabled) {
          return colors.btnDisabledText;
        }
        if (props.$type === 'lightGray') {
          return colors.slate500;
        }
        return colors.btnBlackText;
      }};
    }
  }

  transition: all 0.2s;
`;
